import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth, can } from '../lib/auth';

/**
 * Master files: every Change or Repeat order needs its master file from
 * Quality within one working day of the order coming in.  The list shows what
 * is still to hand over (oldest first, late ones in red), what has been handed
 * over this month, and the month's on-time score - the same score the
 * Performance page shows for Quality.
 *
 * A master file Quality cannot find is marked Not found: recorded with the
 * time and who, and left out of the score.  VPPL, GM and CRM see the page;
 * only Quality marks.
 *
 * API: GET /api/quality/master-files (VIEW_MASTER_FILES), POST / DELETE
 * /api/quality/master-files/:poId (QC_INSPECT).
 */

interface Row {
  poId: string;
  poNumber: string | null;
  orderType: string | null;
  client: string | null;
  jobName: string | null;
  productName: string | null;
  /** Material code (A/W Material No). */
  awNo: string | null;
  designNo: string | null;
  crmName: string | null;
  enteredAt: string;
  dueAt: string;
  handedOverAt: string | null;
  handedOverBy: string | null;
  note: string | null;
  /** Marked not found (handedOverAt is when). */
  notFound: boolean;
  state: 'waiting' | 'late' | 'met' | 'missed' | 'notfound';
}

interface Data {
  ready: boolean;
  /** The server database can record Not found. */
  notFoundReady?: boolean;
  /** Orders in the month marked not found (left out of the score). */
  notFound?: number;
  startsFrom: string;
  target: string;
  now: string;
  score: { total: number; done: number; pending: number; met: number; missed: number; pct: number | null };
  rows: Row[];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM-DD HH:mm' (office time) as "31 Oct, 10:30". */
function when(s: string | null): string {
  if (!s) return '';
  const [d = '', t = ''] = s.split(' ');
  const [, m, day] = d.split('-');
  return `${Number(day)} ${MONTHS[Number(m) - 1]}, ${t}`;
}

/** Minutes between two 'YYYY-MM-DD HH:mm' office times. */
const minutesBetween = (a: string, b: string) =>
  Math.round((Date.parse(`${b.replace(' ', 'T')}:00Z`) - Date.parse(`${a.replace(' ', 'T')}:00Z`)) / 60_000);

function span(min: number): string {
  const m = Math.abs(min);
  if (m < 60) return `${m} min`;
  if (m < 48 * 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${Math.floor(m / (24 * 60))} days`;
}

/** The first and last day of this month or last month, as YYYY-MM-DD. */
function monthRange(offset: 0 | -1, today: string) {
  const y = Number(today.slice(0, 4)), m = Number(today.slice(5, 7));
  const first = new Date(Date.UTC(y, m - 1 + offset, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(first), to: iso(last), label: `${MONTHS[first.getUTCMonth()]} ${first.getUTCFullYear()}` };
}

function friendly(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  if (msg === 'already_handed_over') return 'Someone has already marked this one - the list is refreshed.';
  if (msg === 'master_file_migration_pending') return 'The server database needs its update (master files) first.';
  if (msg === 'master_file_not_found_migration_pending') return 'The server database needs its update (master file not found) first.';
  if (msg === 'not_change_or_repeat') return 'This order is not a Change or Repeat order any more.';
  if (msg === 'order_not_found') return 'This order no longer exists.';
  if (msg === 'forbidden') return 'You do not have permission for this.';
  return msg;
}

const TYPE_STYLE: Record<string, string> = {
  Change: 'bg-amber-50 text-amber-700',
  Repeat: 'bg-sky-50 text-sky-700',
  'Repeat With Change': 'bg-purple-50 text-purple-700',
};

export function MasterFilesPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canMark = can(user, 'QC_INSPECT');
  const [month, setMonth] = useState<0 | -1>(0);
  const [tab, setTab] = useState<'todo' | 'done'>('todo');
  const [open, setOpen] = useState<string | null>(null);
  /** What the open row is being marked as. */
  const [mode, setMode] = useState<'handed' | 'notfound'>('handed');
  const [note, setNote] = useState('');
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);

  // The month is the office's (India), not the browser's.
  const today = new Date(Date.now() + 330 * 60_000).toISOString().slice(0, 10);
  const range = monthRange(month, today);

  const q = useQuery({
    queryKey: ['master-files', range.from, range.to],
    queryFn: () => api<Data>(`/api/quality/master-files?from=${range.from}&to=${range.to}`),
    refetchInterval: 60_000,
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: ['master-files'] });

  const mark = useMutation({
    mutationFn: (r: Row) => api(`/api/quality/master-files/${encodeURIComponent(r.poId)}`, {
      method: 'POST', body: JSON.stringify({ note: note.trim() || undefined, notFound: mode === 'notfound' || undefined }),
    }),
    onSuccess: (_d, r) => {
      setNotice({ ok: true, text: `Master file for ${r.poNumber ?? r.poId} marked ${mode === 'notfound' ? 'not found' : 'handed over'}.` });
      setOpen(null); setNote(''); refresh();
    },
    onError: (e) => { setNotice({ ok: false, text: friendly(e) }); refresh(); },
  });

  const undo = useMutation({
    mutationFn: (r: Row) => api(`/api/quality/master-files/${encodeURIComponent(r.poId)}`, { method: 'DELETE' }),
    onSuccess: (_d, r) => { setNotice({ ok: true, text: `${r.poNumber ?? r.poId} is back in the list to hand over.` }); refresh(); },
    onError: (e) => setNotice({ ok: false, text: friendly(e) }),
  });

  const d = q.data;
  const rows = d?.rows ?? [];
  const todo = rows.filter((r) => !r.handedOverAt).sort((a, b) => a.dueAt.localeCompare(b.dueAt));
  const done = rows.filter((r) => r.handedOverAt).sort((a, b) => (b.handedOverAt ?? '').localeCompare(a.handedOverAt ?? ''));
  const lateNow = todo.filter((r) => r.state === 'late').length;
  const s = d?.score;

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-5xl">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">Master Files</h1>
        <p className="text-sm text-slate-500">
          Every Change and Repeat order needs its master file handed over within one working day
          of the order coming in. Mark it here when you hand it over - the time is recorded then.
          If the master file cannot be found, mark it Not found.
        </p>
      </div>

      {d && !d.ready && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          The server database needs its update (master files) before a handover can be marked.
        </div>
      )}

      {/* The month's score */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-semibold text-slate-700">Score - {range.label}</div>
          <div className="flex gap-1 text-sm">
            {([0, -1] as const).map((m) => (
              <button key={m} onClick={() => setMonth(m)}
                className={`rounded-md px-3 py-1 ${month === m ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}>
                {m === 0 ? 'This month' : 'Last month'}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="On time" value={s?.pct == null ? '-' : `${s.pct}%`}
            tone={s?.pct == null ? 'slate' : s.pct >= 90 ? 'green' : s.pct >= 75 ? 'amber' : 'red'} />
          <Stat label="Handed over on time" value={s?.met ?? '-'} tone="green" />
          <Stat label="Late (or past due)" value={s?.missed ?? '-'} tone="red" />
          <Stat label="Still to hand over" value={s?.pending ?? '-'} tone="amber" />
        </div>
        <p className="mt-2 text-xs text-slate-400">
          Orders entered in {range.label}{d ? `, counted from ${when(`${d.startsFrom} 00:00`).split(',')[0]}` : ''}.
          {' '}Allowed: one working day{d ? ` (${d.target})` : ''} - Sundays and holidays don't count.
          {d?.notFound ? ` ${d.notFound} marked not found - not scored.` : ''}
        </p>
      </div>

      {notice && (
        <div className={`rounded-lg p-3 text-sm ${notice.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}>
          {notice.text}
        </div>
      )}

      <div className="flex gap-2 border-b border-slate-200">
        <TabBtn active={tab === 'todo'} onClick={() => setTab('todo')}>
          To hand over ({todo.length}){lateNow > 0 && <span className="ml-1 rounded bg-red-600 px-1.5 text-xs text-white">{lateNow} late</span>}
        </TabBtn>
        <TabBtn active={tab === 'done'} onClick={() => setTab('done')}>Handed over / not found ({done.length})</TabBtn>
      </div>

      {q.isLoading && <div className="p-4 text-center text-slate-400">Loading…</div>}
      {q.isError && <div className="p-4 text-center text-red-600">{friendly(q.error)}</div>}

      {tab === 'todo' && !q.isLoading && (
        <div className="space-y-2">
          {todo.length === 0 && <div className="rounded-lg bg-slate-50 p-6 text-center text-slate-500">Nothing to hand over. 👍</div>}
          {todo.map((r) => {
            const left = d ? minutesBetween(d.now, r.dueAt) : 0;
            return (
              <div key={r.poId}
                className={`rounded-xl border bg-white p-3 ${r.state === 'late' ? 'border-red-300' : 'border-slate-200'}`}>
                <OrderLines r={r} />
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                  <div className="text-slate-500">
                    In: {when(r.enteredAt)} · Due: <span className="font-medium text-slate-700">{when(r.dueAt)}</span>
                    {' '}
                    {r.state === 'late'
                      ? <span className="ml-1 rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-700">Late by {span(-left)}</span>
                      : <span className="ml-1 rounded bg-emerald-50 px-1.5 py-0.5 text-xs text-emerald-700">{span(left)} left</span>}
                  </div>
                  {canMark && d?.ready && open !== r.poId && (
                    <div className="flex gap-2">
                      {d.notFoundReady && (
                        <button className="btn-ghost border border-slate-300 text-sm"
                          onClick={() => { setOpen(r.poId); setMode('notfound'); setNote(''); setNotice(null); }}>
                          Not found
                        </button>
                      )}
                      <button className="btn-primary text-sm"
                        onClick={() => { setOpen(r.poId); setMode('handed'); setNote(''); setNotice(null); }}>
                        Handed over
                      </button>
                    </div>
                  )}
                </div>
                {open === r.poId && (
                  <div className="mt-2 flex flex-col gap-2 rounded-lg bg-slate-50 p-2 sm:flex-row sm:items-center">
                    <input className="input flex-1 text-sm"
                      placeholder={mode === 'notfound' ? 'Note (optional) - e.g. where you looked' : 'Note (optional) - e.g. given to whom'}
                      maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
                    <div className="flex gap-2">
                      <button className="btn-primary text-sm" disabled={mark.isPending} onClick={() => mark.mutate(r)}>
                        {mark.isPending ? 'Saving…' : mode === 'notfound' ? 'Confirm not found' : 'Confirm handed over'}
                      </button>
                      <button className="btn-ghost text-sm" onClick={() => setOpen(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'done' && !q.isLoading && (
        <div className="space-y-2">
          {done.length === 0 && <div className="rounded-lg bg-slate-50 p-6 text-center text-slate-500">None handed over in {range.label} yet.</div>}
          {done.map((r) => (
            <div key={r.poId} className="rounded-xl border border-slate-200 bg-white p-3">
              <OrderLines r={r} />
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                <div className="text-slate-500">
                  In: {when(r.enteredAt)} · Due: {when(r.dueAt)} · {r.notFound ? 'Marked not found' : 'Handed over'}:{' '}
                  <span className="font-medium text-slate-700">{when(r.handedOverAt)}</span>
                  {r.handedOverBy ? ` by ${r.handedOverBy}` : ''}
                  {' '}
                  {r.state === 'notfound'
                    ? <span className="ml-1 rounded bg-slate-200 px-1.5 py-0.5 text-xs font-semibold text-slate-700">Not found</span>
                    : r.state === 'met'
                      ? <span className="ml-1 rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-700">On time</span>
                      : <span className="ml-1 rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-700">Late</span>}
                  {r.note && <div className="text-xs text-slate-400">Note: {r.note}</div>}
                </div>
                {canMark && (
                  <button className="btn-ghost text-xs text-slate-500" disabled={undo.isPending}
                    onClick={() => {
                      if (confirm(`Undo ${r.notFound ? 'the not found mark' : 'the handover'} of ${r.poNumber ?? r.poId}? Marking it again records the new time.`)) undo.mutate(r);
                    }}>
                    Undo
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OrderLines({ r }: { r: Row }) {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-800">{r.poNumber ?? r.poId}</span>
        {r.orderType && (
          <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${TYPE_STYLE[r.orderType.trim()] ?? 'bg-slate-100 text-slate-600'}`}>
            {r.orderType}
          </span>
        )}
        <span className="text-sm text-slate-600">{r.client}</span>
      </div>
      <div className="mt-0.5 text-sm text-slate-700">{r.productName || r.jobName || '-'}</div>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-sm">
        <span><span className="text-slate-400">Material code:</span> <span className="font-medium text-slate-800">{r.awNo || '-'}</span></span>
        <span><span className="text-slate-400">Design no:</span> <span className="font-medium text-slate-800">{r.designNo || '-'}</span></span>
      </div>
      {r.crmName && <div className="text-xs text-slate-400">CRM: {r.crmName}</div>}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string | number; tone: 'green' | 'red' | 'amber' | 'slate' }) {
  const color = { green: 'text-emerald-700', red: 'text-red-700', amber: 'text-amber-700', slate: 'text-slate-500' }[tone];
  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <div className={`text-2xl font-semibold ${color}`}>{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick}
      className={`-mb-px border-b-2 px-3 py-2 text-sm ${active ? 'border-slate-800 font-semibold text-slate-800' : 'border-transparent text-slate-500'}`}>
      {children}
    </button>
  );
}
